import { useQuery } from '@tanstack/react-query'
import { api, Company } from '../lib/api'
import { Link } from 'react-router-dom'
import { Plus, Building2, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import clsx from 'clsx'

const formatCurrency = (pence: number) => 
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(pence / 100)

const statusColors: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  trialing: 'bg-blue-100 text-blue-700',
  past_due: 'bg-amber-100 text-amber-700',
  canceled: 'bg-red-100 text-red-700'
}

export default function Companies() {
  const [search, setSearch] = useState('')
  
  const { data: companies, isLoading } = useQuery({
    queryKey: ['companies'],
    queryFn: () => api.get<Company[]>('/companies')
  })

  const filtered = companies?.filter(c => 
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.billing_email.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Companies</h1>
        <Link
          to="/companies/new"
          className="flex items-center gap-2 bg-salad-600 text-white px-4 py-2 rounded-lg hover:bg-salad-700 transition-colors"
        >
          <Plus size={20} />
          Add Company
        </Link>
      </div>

      <input
        type="text"
        placeholder="Search companies..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-salad-500 focus:border-transparent"
      />

      {isLoading ? (
        <div className="animate-pulse space-y-4">
          {[1,2,3].map(i => <div key={i} className="h-20 bg-gray-200 rounded-xl" />)}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered?.map(company => (
            <Link
              key={company.id}
              to={`/companies/${company.id}`}
              className="block bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-salad-100 rounded-lg flex items-center justify-center">
                  <Building2 className="text-salad-600" size={24} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900 truncate">{company.name}</h3>
                    <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium', statusColors[company.subscription_status])}>
                      {company.subscription_status}
                    </span>
                  </div>
                  <div className="text-sm text-gray-500">{company.billing_email}</div>
                </div>
                <div className="text-right hidden sm:block">
                  <div className="font-semibold">{company.employee_count} employees</div>
                  <div className="text-sm text-gray-500">{formatCurrency(company.total_funded)} funded</div>
                </div>
                <ChevronRight className="text-gray-400" size={20} />
              </div>
            </Link>
          ))}
          {filtered?.length === 0 && (
            <div className="text-center py-12 text-gray-500">No companies found</div>
          )}
        </div>
      )}
    </div>
  )
}
